import React, { useState } from 'react';

/**
 * Mode of the TransferPanel. Reflects the parent state machine:
 * - `'blind'`: idle. Renders the destination input + three buttons:
 *   "Blind transfer", "Consultar", "Cancelar".
 * - `'consult-input'`: structurally identical to `'blind'`. Kept distinct
 *   so the parent can render different copy if desired without changing the
 *   panel's behavior.
 * - `'consult-active'`: B leg has answered. Renders "Hablando con <target>"
 *   + three buttons: "Completar transferencia", "Conferencia" (F4.4 — only
 *   if onAddToConference is provided), "Cancelar consulta".
 * - `'transferring'`: REFER replaces in flight. Renders "Transfiriendo…".
 * - `'conference-active'` (F4.4): 3-way conference active. Renders both
 *   participants A + B with "Quitar A", "Quitar B", "Cerrar conferencia"
 *   buttons.
 */
export type TransferPanelMode =
  | 'blind'
  | 'consult-input'
  | 'consult-active'
  | 'transferring'
  | 'conference-active';

export interface TransferPanelProps {
  mode: TransferPanelMode;
  defaultCountry: string;
  consultTarget?: string;
  /** F4.4: primary leg's remote identifier (E.164 number or extension), shown in conference-active. */
  primaryRemote?: string;
  onBlindTransfer?: (target: string) => void;
  onStartConsult?: (target: string) => void;
  onCompleteTransfer?: () => void;
  onCancelConsult?: () => void;
  /** F4.4: convert the active consult into a 3-way conference (consulting → conferencing). */
  onAddToConference?: () => void;
  /** F4.4: end the active 3-way conference (conferencing → idle, hangup both). */
  onEndConference?: () => void;
  /** F4.4: drop the primary participant from conference (conferencing → primary-active w/ B). */
  onDropPrimary?: () => void;
  /** F4.4: drop the consult participant from conference (conferencing → primary-active w/ A). */
  onDropConsult?: () => void;
  onCancel: () => void;
}

const EXT_RE = /^\d{2,6}$/;

/**
 * Tiny client-side normalizer used only by the transfer destination input.
 * Mirrors the contract of the IS server-side `normalizeE164` (F4.1-C1):
 * - Returns canonical E.164 if input is already E.164 or matches a known local
 *   pattern + defaultCountry.
 * - Returns the raw input if it looks like a SIP extension (regex \d{2,6}).
 * - Returns null for unparseable inputs.
 *
 * Currently handles ES (9 digits → +34<digits>) and US (10 digits → +1<digits>).
 * Other countries: pass-through E.164 input only.
 */
export function normalizeTransferTarget(input: string, defaultCountry: string): string | null {
  const trimmed = input.trim().replace(/[\s\-()]/g, '');
  if (trimmed === '') return null;
  if (EXT_RE.test(trimmed)) return trimmed; // bare extension
  if (trimmed.startsWith('+')) {
    return /^\+\d{6,15}$/.test(trimmed) ? trimmed : null;
  }
  if (trimmed.startsWith('00')) {
    const e164 = `+${trimmed.slice(2)}`;
    return /^\+\d{6,15}$/.test(e164) ? e164 : null;
  }
  if (defaultCountry === 'ES' && /^\d{9}$/.test(trimmed)) return `+34${trimmed}`;
  if (defaultCountry === 'US' && /^\d{10}$/.test(trimmed)) return `+1${trimmed}`;
  return null;
}

export function TransferPanel(props: TransferPanelProps): React.JSX.Element {
  const [input, setInput] = useState('');
  const normalized = normalizeTransferTarget(input, props.defaultCountry);
  const canSubmit = normalized !== null;

  if (props.mode === 'blind' || props.mode === 'consult-input') {
    return (
      <section role="dialog" aria-label="Transferir llamada">
        <input
          type="text"
          placeholder="Extensión o número (e.g. 2001 o 611111111)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <div>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => normalized && props.onBlindTransfer?.(normalized)}
          >
            Blind transfer
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => normalized && props.onStartConsult?.(normalized)}
          >
            Consultar
          </button>
          <button type="button" onClick={props.onCancel}>
            Cancelar
          </button>
        </div>
      </section>
    );
  }

  if (props.mode === 'consult-active') {
    return (
      <section role="dialog" aria-label="Consulta de transferencia">
        <p>Hablando con {props.consultTarget}</p>
        <button type="button" onClick={() => props.onCompleteTransfer?.()}>
          Completar transferencia
        </button>
        {props.onAddToConference && (
          <button type="button" onClick={() => props.onAddToConference?.()}>
            Conferencia
          </button>
        )}
        <button type="button" onClick={() => props.onCancelConsult?.()}>
          Cancelar consulta
        </button>
      </section>
    );
  }

  if (props.mode === 'conference-active') {
    return (
      <section role="dialog" aria-label="Conferencia 3-way">
        <p>Conferencia activa</p>
        <ul>
          <li>
            Cliente (A) — {props.primaryRemote}{' '}
            <button type="button" onClick={() => props.onDropPrimary?.()}>
              Quitar A
            </button>
          </li>
          <li>
            {props.consultTarget} (B){' '}
            <button type="button" onClick={() => props.onDropConsult?.()}>
              Quitar B
            </button>
          </li>
        </ul>
        <button type="button" onClick={() => props.onEndConference?.()}>
          Cerrar conferencia
        </button>
      </section>
    );
  }

  // transferring
  return (
    <section role="dialog" aria-label="Transfiriendo">
      <p>Transfiriendo…</p>
    </section>
  );
}
